import { GET, POST, PATCH, DELETE, POST_FORM } from "../config";

/**
 * API para mediciones de materias primas (APR)
 */

/**
 * Listar mediciones
 */
export const list = async ({
  page = 1,
  branch = null,
  measurement_point = null,
  product = null,
  reader = null, // ✅ Filtro por operador (usuario que toma la medición)
  start_date = null,
  end_date = null,
  is_verified = null,
  search = null, // ✅ NUEVO: Búsqueda por cliente, código, RUT, código medidor, dirección
  page_size = 10,
} = {}) => {
  const params = new URLSearchParams();
  params.append("page", page);
  params.append("page_size", page_size);

  if (branch) params.append("branch", branch);
  if (measurement_point) params.append("measurement_point", measurement_point);
  if (product) params.append("product", product);
  if (reader) params.append("reader", reader); // ✅ Filtro por operador
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  if (is_verified !== null) params.append("is_verified", is_verified);
  if (search && search.trim()) params.append("search", search.trim()); // ✅ NUEVO: Búsqueda

  const response = await GET(`inventory/measurements/?${params.toString()}`);
  return response.data;
};

/**
 * Obtener una medición específica
 */
export const get = async (id) => {
  const response = await GET(`inventory/measurements/${id}/`);
  return response.data;
};

/**
 * Crear una medición
 */
export const create = async (data) => {
  const response = await POST("inventory/measurements/", data);
  return response.data;
};

/**
 * Crear una medición con orden automática
 * Este endpoint crea la medición y genera automáticamente la orden de venta
 */
export const createWithOrder = async (data) => {
  const response = await POST("inventory/measurements/create_with_order/", data);
  return response;
};

/**
 * Actualizar una medición
 */
export const update = async (id, data) => {
  const response = await PATCH(`inventory/measurements/${id}/`, data);
  return response.data;
};

/**
 * Eliminar una medición
 */
export const destroy = async (id) => {
  const response = await DELETE(`inventory/measurements/${id}/`);
  return response.data;
};

/**
 * Eliminar una medición con todos sus registros relacionados en cascada
 * Elimina: Revenue, movimientos de inventario, Orden y la medición misma
 */
export const deleteWithRelated = async (id) => {
  const { DEVURL } = await import("../config");
  const token = localStorage.getItem("token");
  
  const response = await fetch(`${DEVURL}/inventory/measurements/${id}/delete_with_related/`, {
    method: "DELETE",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `Error al eliminar: ${response.status}`);
  }

  return await response.json();
};

/**
 * Verificar una medición
 */
export const verify = async (id) => {
  const response = await POST(`inventory/measurements/${id}/verify/`);
  return response.data;
};

/**
 * Verificar o desverificar una medición (toggle)
 * @param {number} id - ID de la medición
 * @param {boolean} verify - true para verificar, false para desverificar
 */
export const toggleVerify = async (id, verify) => {
  const response = await POST(`inventory/measurements/${id}/toggle_verify/`, { verify });
  return response.data;
};

/**
 * Obtener orden asociada a una medición
 */
export const getAssociatedOrder = async (measurementId) => {
  try {
    const measurement = await get(measurementId);
    if (measurement.measurement_order_id) {
      const orderResponse = await GET(
        `sales/orders/${measurement.measurement_order_id}/`
      );
      return orderResponse.data;
    }
    return null;
  } catch (error) {
    console.error("Error getting associated order:", error);
    return null;
  }
};

/**
 * Obtener Revenue (ingreso) asociado a una medición
 * El Revenue se obtiene a través de la orden asociada a la medición
 */
export const getAssociatedRevenue = async (measurementId) => {
  try {
    const response = await GET(
      `inventory/measurements/${measurementId}/get_associated_revenue/`
    );
    return response.data;
  } catch (error) {
    console.error("Error getting associated revenue:", error);
    return null;
  }
};

/**
 * Obtener mis mediciones (mediciones del usuario actual)
 * Este endpoint filtra por las mediciones donde el usuario es el reader
 */
export const getMyMeasurements = async ({
  page = 1,
  page_size = 10,
  start_date = null,
  end_date = null,
} = {}) => {
  const params = new URLSearchParams();
  params.append("page", page);
  params.append("page_size", page_size);

  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);

  const response = await GET(`inventory/measurements/my_measurements/?${params.toString()}`);
  return response.data;
};

/**
 * Generar PDF de comprobante de medición
 * POST /inventory/measurements/{id}/generate_pdf/
 */
export const generatePDF = async (id) => {
  const response = await POST(`inventory/measurements/${id}/generate_pdf/`, {});
  return response;
};

/**
 * Descargar PDF de comprobante de medición
 * GET /inventory/measurements/{id}/download_pdf/
 */
export const downloadPDF = async (id) => {
  const { DEVURL } = await import("../config");
  const token = localStorage.getItem("token");
  
  // Usar fetch directamente para evitar problemas con interceptores de Axios
  const url = `${DEVURL}/inventory/measurements/${id}/download_pdf/`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Token ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Error al descargar PDF: ${response.status} ${response.statusText}`);
  }

  // Obtener el blob de la respuesta
  const blob = await response.blob();
  
  // Verificar que sea un PDF
  if (blob.type !== "application/pdf" && !blob.type.includes("pdf")) {
    console.warn("El tipo de archivo recibido no es PDF:", blob.type);
  }
  
  // Crear URL del blob y descargar
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `comprobante_medicion_${id}.pdf`;
  link.style.display = "none";
  
  // Agregar al DOM, hacer click y remover
  document.body.appendChild(link);
  link.click();
  
  // Limpiar después de un pequeño delay para asegurar que la descarga inicie
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }, 100);
};

/**
 * =================================
 * PUNTOS DE MEDICIÓN (Measurement Points)
 * =================================
 */

/**
 * Listar puntos de medición
 */
const listMeasurementPoints = async ({
  page = 1,
  branch = null,
  client = null,
  measurement_type = null,
  is_active = null,
  search = "",
  code__icontains = null,
  client__name__icontains = null,
  location_description__icontains = null,
  page_size = 10,
} = {}) => {
  const params = new URLSearchParams();
  params.append("page", page);
  params.append("page_size", page_size);

  if (branch) params.append("branch", branch);
  if (client) params.append("client", client);
  if (measurement_type) params.append("measurement_type", measurement_type);
  if (is_active !== null && is_active !== undefined) params.append("is_active", is_active);
  if (search) params.append("search", search);
  if (code__icontains) params.append("code__icontains", code__icontains);
  if (client__name__icontains) params.append("client__name__icontains", client__name__icontains);
  if (location_description__icontains) params.append("location_description__icontains", location_description__icontains);

  const response = await GET(
    `inventory/measurement-points/?${params.toString()}`
  );
  return response.data;
};

/**
 * Obtener un punto de medición específico
 */
const getMeasurementPoint = async (id) => {
  const response = await GET(`inventory/measurement-points/${id}/`);
  return response.data;
};

/**
 * Crear un punto de medición
 */
const createMeasurementPoint = async (data) => {
  const response = await POST("inventory/measurement-points/", data);
  return response.data;
};

/**
 * Actualizar un punto de medición
 */
const updateMeasurementPoint = async (id, data) => {
  const response = await PATCH(`inventory/measurement-points/${id}/`, data);
  return response.data;
};

/**
 * Eliminar un punto de medición
 */
const destroyMeasurementPoint = async (id) => {
  const response = await DELETE(`inventory/measurement-points/${id}/`);
  return response.data;
};

/**
 * Obtener historial de mediciones de un punto
 */
const getMeasurementPointHistory = async (id, days = 365) => {
  const response = await GET(
    `inventory/measurement-points/${id}/history/?days=${days}`
  );
  return response.data;
};

/**
 * Obtener estadísticas de puntos de medición
 */
const getMeasurementPointStats = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.branch) params.append("branch", filters.branch);
  if (filters.client) params.append("client", filters.client);
  if (filters.measurement_type) params.append("measurement_type", filters.measurement_type);
  if (filters.is_active !== undefined && filters.is_active !== null) {
    params.append("is_active", filters.is_active);
  }
  if (filters.search) params.append("search", filters.search);
  if (filters.code__icontains) params.append("code__icontains", filters.code__icontains);
  if (filters.client__name__icontains) params.append("client__name__icontains", filters.client__name__icontains);
  if (filters.location_description__icontains) params.append("location_description__icontains", filters.location_description__icontains);

  const queryString = params.toString();
  const url = queryString
    ? `inventory/measurement-points/stats/?${queryString}`
    : `inventory/measurement-points/stats/`;

  const response = await GET(url);
  return response.data;
};

const bulkImportMeasurementPoints = async (file, branch = null) => {
  let url = "inventory/measurement-points/bulk_import/";
  if (branch) {
    url += `?branch=${branch}`;
  }

  const response = await POST_FORM(url, {
    file,
  });
  return response.data;
};

/**
 * Exportar measurementPoints como objeto
 */
export const measurementPoints = {
  list: listMeasurementPoints,
  get: getMeasurementPoint,
  create: createMeasurementPoint,
  update: updateMeasurementPoint,
  destroy: destroyMeasurementPoint,
  delete: destroyMeasurementPoint,
  getHistory: getMeasurementPointHistory,
  getStats: getMeasurementPointStats,
   bulkImport: bulkImportMeasurementPoints,
};

/**
 * =================================
 * TIPOS DE MEDICIÓN (Measurement Types)
 * ⚠️ DEPRECATED - Este modelo fue eliminado del backend
 * =================================
 * 
 * El modelo MeasurementType fue eliminado y reemplazado por Product con
 * product_type="MEASUREMENT_MATERIAL".
 * 
 * En su lugar, usar:
 * - api.products.getMeasurementMaterials() para obtener materias de medición
 * - Los productos tienen: name, measurement_unit, category, etc.
 * 
 * Este código se mantiene temporalmente para compatibilidad pero
 * retornará errores 404 si se usa.
 */

/**
 * @deprecated Usar api.products.getMeasurementMaterials() en su lugar
 */
const listMeasurementTypes = async ({
  page = 1,
  unit = null,
  is_active = null,
  search = "",
  page_size = 100,
} = {}) => {
  console.warn(
    "[DEPRECATED] measurementTypes.list() está obsoleto. " +
    "Usar api.products.getMeasurementMaterials() en su lugar."
  );
  
  // Este endpoint ya no existe en el backend
  throw new Error(
    "MeasurementType fue eliminado. Usar api.products.getMeasurementMaterials() para obtener materias de medición."
  );
};

/**
 * @deprecated Usar api.products.get() con productos de tipo MEASUREMENT_MATERIAL
 */
const getMeasurementType = async (id) => {
  console.warn(
    "[DEPRECATED] measurementTypes.get() está obsoleto. " +
    "Usar api.products.get() en su lugar."
  );
  throw new Error("MeasurementType fue eliminado. Usar api.products.get() en su lugar.");
};

/**
 * @deprecated Crear productos con product_type="MEASUREMENT_MATERIAL"
 */
const createMeasurementType = async (data) => {
  console.warn(
    "[DEPRECATED] measurementTypes.create() está obsoleto. " +
    "Crear productos con product_type='MEASUREMENT_MATERIAL' en su lugar."
  );
  throw new Error("MeasurementType fue eliminado. Crear productos de tipo MEASUREMENT_MATERIAL.");
};

/**
 * @deprecated Actualizar productos directamente
 */
const updateMeasurementType = async (id, data) => {
  console.warn(
    "[DEPRECATED] measurementTypes.update() está obsoleto. " +
    "Usar api.products.update() en su lugar."
  );
  throw new Error("MeasurementType fue eliminado. Usar api.products.update() en su lugar.");
};

/**
 * @deprecated Eliminar productos directamente
 */
const destroyMeasurementType = async (id) => {
  console.warn(
    "[DEPRECATED] measurementTypes.destroy() está obsoleto. " +
    "Usar api.products.delete() en su lugar."
  );
  throw new Error("MeasurementType fue eliminado. Usar api.products.delete() en su lugar.");
};

/**
 * @deprecated Usar api.products.getMeasurementMaterials() con filtro is_active
 */
const getActiveMeasurementTypes = async () => {
  console.warn(
    "[DEPRECATED] measurementTypes.getActive() está obsoleto. " +
    "Usar api.products.getMeasurementMaterials() en su lugar."
  );
  throw new Error("MeasurementType fue eliminado. Usar api.products.getMeasurementMaterials().");
};

/**
 * ⚠️ DEPRECATED - Exportar measurementTypes como objeto
 * 
 * Este objeto se mantiene solo para evitar errores de importación,
 * pero todas las funciones lanzarán errores indicando que deben
 * usar la API de productos en su lugar.
 */
export const measurementTypes = {
  list: listMeasurementTypes,
  get: getMeasurementType,
  create: createMeasurementType,
  update: updateMeasurementType,
  destroy: destroyMeasurementType,
  delete: destroyMeasurementType,
  getActive: getActiveMeasurementTypes,
};
